/**
 * Client-side mirror of the server's scope predicates.
 *
 * Grants come from `GET /v1/me` (the server-resolved `Identity`), never from
 * decoding the token here: an opaque static operator bearer carries no readable
 * claims, so `decodeJwtPayload` would report the operator as read-only. Every
 * predicate below is pure — it takes the grants rather than reaching for a token,
 * which is what makes it testable and keeps the fetch in exactly one place.
 *
 * Hiding a control is an affordance, not authorization. The server is the
 * authority; a 403 is surfaced inline where it happens.
 */

export const SCOPE_READ = "kw:read";
export const SCOPE_INGEST = "kw:ingest";
export const SCOPE_REALM_ADMIN = "kw:realm-admin";
export const SCOPE_ORG_ADMIN = "kw:org-admin";
export const SCOPE_PLATFORM_ADMIN = "kw:platform-admin";

export interface Grants {
  scopes: string[];
  /** Realms the subject is granted or a member of (`identity.realm_names()`). */
  realms: string[];
}

export const EMPTY_GRANTS: Grants = { scopes: [], realms: [] };

/**
 * Mirrors `Identity.has_scope`: `kw:platform-admin` is a wildcard, and
 * `kw:org-admin` satisfies a `kw:realm-admin` gate. Read scopes grant no admin
 * authority — deliberately not a general hierarchy.
 */
export function hasScope(g: Grants, scope: string): boolean {
  if (g.scopes.includes(SCOPE_PLATFORM_ADMIN)) return true;
  if (g.scopes.includes(scope)) return true;
  if (scope === SCOPE_REALM_ADMIN && g.scopes.includes(SCOPE_ORG_ADMIN)) return true;
  return false;
}

export function isPlatformAdmin(g: Grants): boolean {
  return hasScope(g, SCOPE_PLATFORM_ADMIN);
}

export function isOrgAdmin(g: Grants): boolean {
  return hasScope(g, SCOPE_ORG_ADMIN);
}

export function isRealmAdmin(g: Grants): boolean {
  return hasScope(g, SCOPE_REALM_ADMIN);
}

/**
 * Mirrors `require_realm_admin_for`: platform-admin anywhere, otherwise the
 * realm-admin tier *and* membership of that realm. Same rule on both sides so
 * the UI can predict what the server will do.
 */
export function canAdminRealm(g: Grants, realmId: string): boolean {
  if (isPlatformAdmin(g)) return true;
  return isRealmAdmin(g) && g.realms.includes(realmId);
}

/** BYOK credentials on a provider binding stay platform-admin-only (D-LLM-4). */
export function canByok(g: Grants): boolean {
  return isPlatformAdmin(g);
}

/**
 * Mirrors `may_use_platform_profiles`: a reserved Render Profile (the kontextwerk
 * house style) is bindable and pinnable only by a platform-admin (D-RENDER-16).
 *
 * Scope-identical to `canByok` today and deliberately its own predicate anyway —
 * they answer different questions ("may this caller supply credentials?" vs. "may
 * this realm wear the platform's own brand?"), and reading one as the other is
 * how a later change to either silently moves the wrong gate.
 */
export function canUsePlatformProfiles(g: Grants): boolean {
  return isPlatformAdmin(g);
}

export type EffectiveRole = "platform-admin" | "org-admin" | "realm-admin" | "read-only";

export function effectiveRole(g: Grants): EffectiveRole {
  if (isPlatformAdmin(g)) return "platform-admin";
  if (g.scopes.includes(SCOPE_ORG_ADMIN)) return "org-admin";
  if (g.scopes.includes(SCOPE_REALM_ADMIN)) return "realm-admin";
  return "read-only";
}
